import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { clientsLookup, brandsLookup, projectsLookup } from "@/lib/lookupCache";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MultiSelect from "@/components/ui/multi-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import React from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Sparkles, List, LayoutGrid, Clock, Rows3, Newspaper, CalendarClock, Trash2, ListChecks, Link2, User as UserIcon, Search, ExternalLink, BarChart3, PackageOpen, CalendarRange, MessageSquare, GanttChartSquare, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { CATEGORIES } from "@/pages/app/Packages";
import SmartPostEditor from "@/pages/app/SmartPostEditor";
import MonthTimeline from "@/pages/app/MonthTimeline";
import ContentCalendarSummary from "@/pages/app/ContentCalendarSummary";
import PackageDeliveryReport from "@/pages/app/PackageDeliveryReport";
import ContentCalendarTimeline from "@/pages/app/ContentCalendarTimeline";
import ContentCalendarGantt from "@/pages/app/ContentCalendarGantt";
import TimelineInsights from "@/pages/app/TimelineInsights";
import { TypeIcon } from "@/lib/formatIcon";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { TaskDetail } from "@/pages/app/Tasks";
import PackageCalendarSyncDialog from "@/components/packages/PackageCalendarSyncDialog";
import AllPackagesCalendarSyncDialog from "@/components/packages/AllPackagesCalendarSyncDialog";
import PackageHoverPreview from "@/components/packages/PackageHoverPreview";
import WorkloadModal from "@/components/calendar/WorkloadModal";

export const ITEM_STATUSES = ["planned", "briefed", "in_production", "internal_qc", "client_review", "on_hold", "blocked", "approved", "scheduled", "published", "cancelled"];
export const STATUS_TONES = {
  planned: "bg-muted text-foreground/70", briefed: "bg-sky-100 text-sky-800", in_production: "bg-amber-100 text-amber-800",
  internal_qc: "bg-violet-100 text-violet-800", client_review: "bg-fuchsia-100 text-fuchsia-800",
  on_hold: "bg-yellow-100 text-yellow-900 border border-yellow-400",
  blocked: "bg-red-100 text-red-800 border border-red-500",
  approved: "bg-emerald-100 text-emerald-800", scheduled: "bg-teal-100 text-teal-800",
  published: "bg-emerald-600 text-white", cancelled: "bg-red-100 text-red-700",
};
const STATUS_LABEL = (s) => (s || "").replaceAll("_", " ");
const monthStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export default function ContentCalendar() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [month, setMonth] = useState(monthStr(new Date()));
  const [items, setItems] = useState(null);
  // Iter · Feb 2026 — Visible loading state for the heavy month load.
  // Before this, the whole page just blanked for 1-3s while the
  // enriched endpoint ran. Now a shimmer strip up top + a skeleton
  // list underneath tells the user we're actually working.
  const [itemsLoading, setItemsLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  // R97 · Multi-value filter state. Backend endpoint accepts a single value
  // per key today, so when 2+ picks are made we omit the server param and
  // filter client-side. `MonthTimeline` still receives the first pick as
  // its scalar `brandFilter` prop for backward compat.
  const [brandF, setBrandF] = useState([]);
  const [statusF, setStatusF] = useState([]);
  const [catF, setCatF] = useState([]);
  // Iter · Feb 2026 · R260 — Project filter chip. Uses the same
  // multi-select pattern as brand/status/category and applies
  // client-side against the `project_id` / `project_names` fields
  // enriched by the backend.
  const [projF, setProjF] = useState([]);
  const [projects, setProjects] = useState([]);
  // R268uuu · Feb 2026 — Two independent assignee filters on the
  // Content Calendar: "Primary" (the deliverable owner) and "Task"
  // (any assignee on a linked task). Both accept multi-select and
  // AND with the other chips.
  const [primaryAssigneeF, setPrimaryAssigneeF] = useState([]);
  const [taskAssigneeF, setTaskAssigneeF] = useState([]);
  // R268~2 · Feb 2026 — Explicit APPLY button. Filter chips used to
  // trigger a fresh `/content-items-enriched` fetch on every change,
  // which was firing 6-8 requests as a PM tuned their view. Now every
  // MultiSelect binds to a "staged*" state; only clicking Apply copies
  // the stage into the applied state (`*F`) which the loader depends
  // on. Removes the request storm entirely.
  const [stagedBrand, setStagedBrand] = useState([]);
  const [stagedStatus, setStagedStatus] = useState([]);
  const [stagedCat, setStagedCat] = useState([]);
  const [stagedProj, setStagedProj] = useState([]);
  const [stagedPrimaryAssignee, setStagedPrimaryAssignee] = useState([]);
  const [stagedTaskAssignee, setStagedTaskAssignee] = useState([]);
  const arrEq = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
  const filtersDirty = !(arrEq(stagedBrand, brandF)
    && arrEq(stagedStatus, statusF)
    && arrEq(stagedCat, catF)
    && arrEq(stagedProj, projF)
    && arrEq(stagedPrimaryAssignee, primaryAssigneeF)
    && arrEq(stagedTaskAssignee, taskAssigneeF));
  const applyFilters = () => {
    setBrandF(stagedBrand);
    setStatusF(stagedStatus);
    setCatF(stagedCat);
    setProjF(stagedProj);
    setPrimaryAssigneeF(stagedPrimaryAssignee);
    setTaskAssigneeF(stagedTaskAssignee);
  };
  const resetFilters = () => {
    setStagedBrand([]); setStagedStatus([]); setStagedCat([]); setStagedProj([]);
    setStagedPrimaryAssignee([]); setStagedTaskAssignee([]);
    setBrandF([]); setStatusF([]); setCatF([]); setProjF([]);
    setPrimaryAssigneeF([]); setTaskAssigneeF([]);
  };
  const [genOpen, setGenOpen] = useState(false);
  // Feb 2026 · Ad-hoc deliverable dialog — a PM can drop a one-off item
  // onto the calendar without touching any package schedule.
  const [newItemOpen, setNewItemOpen] = useState(false);
  // Iter · Feb 2026 — Floating Workload panel. Lives at the page level
  // (not inside a tab) so a PM can open it from ANY calendar tab and
  // keep it visible while dragging/resizing to compare dates and users.
  const [workloadOpen, setWorkloadOpen] = useState(false);
  // Feb 2026 · Compare-with-package dialog
  const [comparePkgOpen, setComparePkgOpen] = useState(false);
  const [comparePkgId, setComparePkgId] = useState("");
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  // Feb 2026 — Track which SmartPostEditor tab should open first when
  // an item is opened. `null` → default (Details). "timeline" → the
  // combined Activity + Comments tab, driven by the new action-column
  // button in the list view.
  const [selectedTab, setSelectedTab] = useState(null);
  const openItemAt = (item, tab = null) => {
    setSelectedTab(tab);
    setSelected(item);
  };
  const [taskDetail, setTaskDetail] = useState(null);   // full task obj for TaskDetail dialog
  const [members, setMembers] = useState([]);

  const openTask = async (tid) => {
    if (!tid) return;
    try {
      const { data } = await api.get(`/tasks/${tid}`);
      setTaskDetail(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't open task")); }
  };
  const patchOpenTask = async (patch) => {
    if (!taskDetail) return;
    try {
      await api.patch(`/tasks/${taskDetail.id}`, patch);
      setTaskDetail((d) => d ? { ...d, ...patch } : d);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const load = async () => {
    if (!wsId) return;
    const reqMonth = month;
    setItemsLoading(true);
    const p = new URLSearchParams({ month, limit: "5000" });
    // R97 · Server accepts one value → send the pick only when exactly one.
    if (brandF.length === 1) p.set("brand_id", brandF[0]);
    if (statusF.length === 1) p.set("status", statusF[0]);
    if (catF.length === 1) p.set("category", catF[0]);
    try {
      const r = await api.get(`/workspaces/${wsId}/content-items-enriched?${p.toString()}`);
      let rows = r.data?.rows || r.data || [];
      // Client-side filter when multiple picks are active.
      if (brandF.length > 1) rows = rows.filter((it) => brandF.includes(it.brand_id));
      if (statusF.length > 1) rows = rows.filter((it) => statusF.includes(it.status));
      if (catF.length > 1) rows = rows.filter((it) => catF.includes(it.category));
      // Feb 2026 · R260 · Project filter is always client-side (endpoint
      // doesn't accept project_id) — match against scalar project_id and
      // the project_names array so tasks-only projects also work.
      if (projF.length > 0) {
        rows = rows.filter((it) => {
          if (it.project_id && projF.includes(it.project_id)) return true;
          const names = new Set(it.project_names || []);
          // Fallback: match by name when project_id absent.
          const selNames = projects.filter((p) => projF.includes(p.id)).map((p) => p.name);
          return selNames.some((n) => names.has(n));
        });
      }
      // R268uuu · Primary-assignee filter — matches the deliverable's
      // own owner. Rows with no owner never match.
      if (primaryAssigneeF.length > 0) {
        rows = rows.filter((it) => primaryAssigneeF.includes(it.primary_assignee_user_id));
      }
      // R268uuu · Task-assignee filter — matches ANY linked task's
      // assignee. Deliverables without linked tasks never match.
      if (taskAssigneeF.length > 0) {
        rows = rows.filter((it) => (it.tasks || []).some((t) => taskAssigneeF.includes(t.assignee_id)));
      }
      setItems((prev) => (reqMonth === monthRef.current ? rows : prev));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); setItems([]); }
    finally { if (reqMonth === monthRef.current) setItemsLoading(false); }
  };
  const monthRef = useMemo(() => ({ current: month }), []);
  monthRef.current = month;
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, month, brandF, statusF, catF, projF, primaryAssigneeF, taskAssigneeF]);

  // Feb 2026 · Deep-link `?open=<item_id>` from the Tasks list (and
  // elsewhere). On mount — and whenever the query-string changes — fetch
  // the deliverable, hop to the right month if needed, and open the
  // SmartPostEditor with that item selected. The query param is scrubbed
  // once the dialog opens so refresh doesn't re-trigger it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || !wsId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: it } = await api.get(`/content-items/${openId}`);
        if (cancelled || !it) return;
        // Jump to the item's month so the Month/List view actually
        // contains it after the dialog is closed.
        if (it.planned_date) {
          const itMonth = it.planned_date.slice(0, 7);
          if (itMonth !== month) setMonth(itMonth);
        }
        // Feb 2026 · Allow deep-links to a specific SmartPostEditor
        // tab (e.g. `?open=<cid>&tab=timeline` from the Content Sheet
        // review-comments icon lands the user straight on the
        // Activity + Comments thread).
        const tab = searchParams.get("tab");
        if (tab) setSelectedTab(tab);
        setSelected(it);
      } catch (e) {
        toast.error(apiErrorMessage(e, "Deliverable not found"));
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete("open");
          next.delete("tab");
          setSearchParams(next, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, searchParams]);
  useEffect(() => { if (wsId) brandsLookup(wsId).then((rows) => setBrands(rows || [])); }, [wsId]);
  useEffect(() => { if (wsId) api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => {}); }, [wsId]);
  useEffect(() => {
    if (!wsId) return;
    projectsLookup(wsId).then((rows) => setProjects((rows || []).map((p) => ({ id: p.id, name: p.name }))));
  }, [wsId]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/packages`)
      .then((r) => setPackages((r.data?.rows || r.data || []).filter((p) => p.status === "active")))
      .catch(() => setPackages([]));
  }, [wsId]);

  // Feb 2026 · Workspace-managed category list. The Content Calendar
  // filter was previously hardcoded to the legacy CATEGORIES constant
  // (social / seo / ads / content / web / email / meeting /
  // documentation / adhoc / other), ignoring everything users curated
  // via Packages → Manage categories (Reel, Carousel, Blog, GMB, …).
  // Now we fetch the workspace rows + union with whatever categories
  // the loaded items *actually* use so the filter always covers the
  // visible data without dropping legacy strings on records that
  // haven't been migrated yet.
  const [wsCategories, setWsCategories] = useState([]);
  useEffect(() => {
    if (!wsId) { setWsCategories([]); return; }
    api.get(`/workspaces/${wsId}/content-categories`)
      .then((r) => setWsCategories(
        (r.data || [])
          .filter((c) => c.active !== false)
          .map((c) => ({ name: c.name, color: c.color || null, legacy: !!c.legacy }))
      ))
      .catch(() => setWsCategories([]));
  }, [wsId]);
  // Merged, de-duped, alphabetically sorted list of category options
  // for the Content Calendar filter. Case-insensitive dedupe so
  // "Reel" vs "reel" don't both appear.
  const categoryOptions = useMemo(() => {
    const seen = new Map(); // lowercase name → display name
    for (const c of wsCategories) {
      const key = (c.name || "").toLowerCase();
      if (key && !seen.has(key)) seen.set(key, c.name);
    }
    for (const it of items || []) {
      const name = it?.category;
      if (!name) continue;
      const key = String(name).toLowerCase();
      if (!seen.has(key)) seen.set(key, name);
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [wsCategories, items]);

  // Iter · Feb 2026 · R173 — Package-vs-Calendar gap banner. Every time
  // the workspace or month changes, run a diff to see how many
  // deliverables from ACTIVE packages haven't been generated yet on
  // the calendar. When > 0 we surface a bright banner with a one-click
  // "Sync now" that opens the workspace-wide sync dialog so users
  // don't have to hunt for the "Compare with package" button.
  const [pkgGap, setPkgGap] = useState(null); // { total_missing, packages_with_gap, packages, totals }
  useEffect(() => {
    if (!wsId || !month) { setPkgGap(null); return; }
    let cancelled = false;
    api.get(`/workspaces/${wsId}/content-calendar/all-packages-diff?month=${month}`)
      .then((r) => { if (!cancelled) setPkgGap(r.data); })
      .catch(() => { if (!cancelled) setPkgGap(null); });
    return () => { cancelled = true; };
  }, [wsId, month, items]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(monthStr(d));
  };
  const [yy, mm] = month.split("-").map(Number);
  const monthLabel = new Date(yy, mm - 1, 1).toLocaleString("en", { month: "long", year: "numeric" });

  return (
    <>
      <PageHeader
        eyebrow="Delivery"
        title="Content Calendar"
        density="dense"
        description="Every deliverable across brands — auto-generated from active packages, tracked to publish."
        testid="content-calendar-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setWorkloadOpen(true)} data-testid="cc-workload-btn" title="Open the Workload chart — draggable overlay, works across every tab">
              <BarChart3 className="w-4 h-4 mr-2" /> Workload
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => setComparePkgOpen(true)} data-testid="cc-compare-package-btn" title="Compare a package against the calendar and create missing items">
              <CalendarRange className="w-4 h-4 mr-2" /> Compare with package
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => setNewItemOpen(true)} data-testid="cc-new-item-btn" title="Add a one-off deliverable not tied to any package schedule">
              <Plus className="w-4 h-4 mr-2" /> New deliverable
            </Button>
            <Button className="rounded-none" onClick={() => setGenOpen(true)} data-testid="cc-generate-btn"><Sparkles className="w-4 h-4 mr-2" /> Generate month</Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        {/* Iter · Feb 2026 — visible shimmer when the heavy monthly
            query is in flight. Pinned up top so it's visible from
            every tab without displacing content. */}
        {itemsLoading && (
          <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 md:-mx-10 h-1 bg-primary/10 overflow-hidden"
                role="status" aria-live="polite"
                data-testid="cc-loading-strip">
            <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-primary to-transparent animate-[cc-shimmer_1.2s_ease-in-out_infinite]" />
            <style>{`@keyframes cc-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center border border-border">
            <Button variant="ghost" size="sm" className="rounded-none h-9" onClick={() => shiftMonth(-1)} data-testid="cc-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="px-3 text-sm font-semibold w-40 text-center" data-testid="cc-month-label">{monthLabel}</span>
            <Button variant="ghost" size="sm" className="rounded-none h-9" onClick={() => shiftMonth(1)} data-testid="cc-next-month"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <MultiSelect
            options={brands.map((b) => ({ value: b.id, label: b.name }))}
            value={stagedBrand}
            onChange={setStagedBrand}
            allLabel="All brands"
            placeholder="Brand"
            width="w-48"
            testid="cc-brand-filter"
          />
          <MultiSelect
            options={ITEM_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL(s) }))}
            value={stagedStatus}
            onChange={setStagedStatus}
            allLabel="All statuses"
            placeholder="Status"
            width="w-44"
            testid="cc-status-filter"
          />
          <MultiSelect
            options={categoryOptions.map((c) => ({ value: c, label: c }))}
            value={stagedCat}
            onChange={setStagedCat}
            allLabel="All categories"
            placeholder="Category"
            width="w-40"
            testid="cc-cat-filter"
          />
          <MultiSelect
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            value={stagedProj}
            onChange={setStagedProj}
            allLabel="All projects"
            placeholder="Project"
            width="w-48"
            testid="cc-proj-filter"
          />
          {/* R268uuu · Feb 2026 — Primary + Task assignee filters.
              Both derive their options from the workspace `members`
              list (sorted alphabetically by name), and MultiSelect
              already renders a search-as-you-type input in the
              dropdown, so PMs can type "sarah" to narrow the picker. */}
          <MultiSelect
            options={[...(members || [])]
              .map((m) => ({ value: m.user_id || m.id, label: m.name || m.email || "—" }))
              .filter((o) => o.value)
              .sort((a, b) => (a.label || "").localeCompare(b.label || ""))}
            value={stagedPrimaryAssignee}
            onChange={setStagedPrimaryAssignee}
            allLabel="All primary assignees"
            placeholder="Primary assignee"
            width="w-52"
            testid="cc-primary-assignee-filter"
            searchable
          />
          <MultiSelect
            options={[...(members || [])]
              .map((m) => ({ value: m.user_id || m.id, label: m.name || m.email || "—" }))
              .filter((o) => o.value)
              .sort((a, b) => (a.label || "").localeCompare(b.label || ""))}
            value={stagedTaskAssignee}
            onChange={setStagedTaskAssignee}
            allLabel="All task assignees"
            placeholder="Task assignee"
            width="w-48"
            testid="cc-task-assignee-filter"
            searchable
          />
          {/* R268~2 · Feb 2026 — Apply + Reset controls. Apply becomes
              primary when staged values differ from what's currently
              loaded. Reset always clears both staged and applied. */}
          <Button
            size="sm"
            className="rounded-none h-9 text-xs"
            onClick={applyFilters}
            disabled={!filtersDirty}
            variant={filtersDirty ? "default" : "outline"}
            data-testid="cc-apply-filters"
            title={filtersDirty ? "Apply pending filter changes" : "No pending filter changes"}
          >
            {filtersDirty ? "Apply filters •" : "Apply"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-none h-9 text-xs text-muted-foreground hover:text-foreground"
            onClick={resetFilters}
            data-testid="cc-reset-filters"
            title="Clear all filters"
          >
            Reset
          </Button>
          <span className="text-xs text-muted-foreground ml-auto" data-testid="cc-count">{items ? `${items.length} deliverables` : "…"}</span>
        </div>

        {pkgGap && pkgGap.totals && pkgGap.totals.missing > 0 && (
          <div className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 flex items-center gap-3 flex-wrap"
               data-testid="cc-pkg-gap-banner">
            <div className="flex items-center gap-2 min-w-0">
              <CalendarRange className="w-4 h-4 text-amber-700 shrink-0" />
              <div className="text-[12px] leading-tight">
                <div className="font-semibold text-amber-900 dark:text-amber-200">
                  <span data-testid="cc-pkg-gap-missing-count">{pkgGap.totals.missing}</span>
                  {" "}deliverable{pkgGap.totals.missing === 1 ? "" : "s"} missing from{" "}
                  <span data-testid="cc-pkg-gap-package-count">{pkgGap.packages_with_gap}</span>
                  {" "}active package{pkgGap.packages_with_gap === 1 ? "" : "s"} this month.
                </div>
                <div className="text-[10px] text-amber-800/80 dark:text-amber-200/70">
                  {pkgGap.totals.existing} generated · {pkgGap.totals.expected} expected across {pkgGap.packages_scanned} active packages
                </div>
              </div>
            </div>
            <Button size="sm" className="h-7 rounded-none ml-auto bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => setComparePkgOpen(true)}
                    data-testid="cc-pkg-gap-sync-btn">
              Sync missing now →
            </Button>
          </div>
        )}

        <Tabs defaultValue="list" className="w-full">
          <TabsList className="rounded-none flex-wrap h-auto justify-start">
            <TabsTrigger value="list" className="rounded-none" data-testid="cc-view-list"><List className="w-3.5 h-3.5 mr-1.5" /> List</TabsTrigger>
            <TabsTrigger value="month" className="rounded-none" data-testid="cc-view-month"><LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Month</TabsTrigger>
            <TabsTrigger value="week" className="rounded-none" data-testid="cc-view-week"><Rows3 className="w-3.5 h-3.5 mr-1.5" /> Weekly</TabsTrigger>
            <TabsTrigger value="day" className="rounded-none" data-testid="cc-view-day"><Clock className="w-3.5 h-3.5 mr-1.5" /> Daily</TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-none" data-testid="cc-view-timeline"><CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Timeline</TabsTrigger>
            <TabsTrigger value="gantt" className="rounded-none" data-testid="cc-view-gantt"><GanttChartSquare className="w-3.5 h-3.5 mr-1.5" /> Gantt</TabsTrigger>
            <TabsTrigger value="hourly" className="rounded-none" data-testid="cc-view-hourly"><Clock className="w-3.5 h-3.5 mr-1.5" /> 24h grid</TabsTrigger>
            <TabsTrigger value="feed" className="rounded-none" data-testid="cc-view-feed"><Newspaper className="w-3.5 h-3.5 mr-1.5" /> Feed</TabsTrigger>
            <TabsTrigger value="upcoming" className="rounded-none" data-testid="cc-view-upcoming"><CalendarClock className="w-3.5 h-3.5 mr-1.5" /> Upcoming</TabsTrigger>
            <TabsTrigger value="summary" className="rounded-none" data-testid="cc-view-summary"><BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Summary</TabsTrigger>
            <TabsTrigger value="pkg_delivery" className="rounded-none" data-testid="cc-view-pkg-delivery"><PackageOpen className="w-3.5 h-3.5 mr-1.5" /> Package Delivery</TabsTrigger>
          </TabsList>
          <TabsContent value="list" className="pt-4"><ListView items={items} onOpen={setSelected} onOpenActivity={(it) => openItemAt(it, "timeline")} onOpenTask={openTask} onChanged={load} members={members} /></TabsContent>
          <TabsContent value="month" className="pt-4"><MonthView items={items} year={yy} month={mm} onOpen={setSelected} onOpenTask={openTask} onReloaded={load} members={members} /></TabsContent>
          <TabsContent value="week" className="pt-4"><WeekView items={items} year={yy} month={mm} onOpen={setSelected} onOpenTask={openTask} members={members} onReloaded={load} /></TabsContent>
          <TabsContent value="day" className="pt-4"><DayView items={items} year={yy} month={mm} onOpen={setSelected} onOpenTask={openTask} members={members} onReloaded={load} /></TabsContent>
          <TabsContent value="timeline" className="pt-4"><ContentCalendarTimeline wsId={wsId} initialMonth={month} onOpen={setSelected} onOpenTask={openTask} members={members} brandF={brandF} statusF={statusF} catF={catF} projF={projF} projects={projects} /></TabsContent>
          <TabsContent value="gantt" className="pt-4"><ContentCalendarGantt wsId={wsId} items={items || []} month={month} brands={brands} projects={projects} packages={packages} members={members} onOpen={setSelected} onOpenTask={openTask} onReloaded={load} /></TabsContent>
          <TabsContent value="hourly" className="pt-4"><MonthTimeline wsId={wsId} month={month} brandFilter={brandF.length === 1 ? brandF[0] : "_all"} onOpen={setSelected} /></TabsContent>
          <TabsContent value="feed" className="pt-4"><FeedView items={items} onOpen={setSelected} /></TabsContent>
          <TabsContent value="upcoming" className="pt-4"><UpcomingView wsId={wsId} onOpen={setSelected} /></TabsContent>
          <TabsContent value="summary" className="pt-4"><ContentCalendarSummary wsId={wsId} brands={brands} currentMonth={month} /></TabsContent>
          <TabsContent value="pkg_delivery" className="pt-4"><PackageDeliveryReport wsId={wsId} currentMonth={month} /></TabsContent>
        </Tabs>
      </div>
      {genOpen && <GenerateDialog wsId={wsId} defaultMonth={month} brands={brands} onClose={() => setGenOpen(false)} onGenerated={load} />}
      {newItemOpen && <NewManualItemDialog
        wsId={wsId}
        defaultMonth={month}
        brands={brands}
        projects={projects}
        packages={packages}
        members={members}
        onClose={() => setNewItemOpen(false)}
        onCreated={async () => { await load(); setNewItemOpen(false); }}
      />}
      <AllPackagesCalendarSyncDialog
        open={comparePkgOpen}
        onClose={() => setComparePkgOpen(false)}
        workspaceId={wsId}
        defaultMonth={month}
        onSynced={load}
      />
      <PackageCalendarSyncDialog
        open={!!comparePkgId}
        onClose={() => setComparePkgId("")}
        workspaceId={wsId}
        packageId={comparePkgId}
        packageName={(packages.find((p) => p.id === comparePkgId) || {}).name}
        defaultMonth={month}
        onSynced={load}
      />
      {selected && <SmartPostEditor item={selected} initialTab={selectedTab} onClose={() => { setSelected(null); setSelectedTab(null); }} onChanged={async () => { await load(); setSelected(null); setSelectedTab(null); }} />}
      {taskDetail && (
        <TaskDetail
          task={taskDetail}
          members={members}
          allTasks={[]}
          onClose={() => setTaskDetail(null)}
          onPatch={patchOpenTask}
        />
      )}
      <WorkloadModal
        open={workloadOpen}
        onClose={() => setWorkloadOpen(false)}
        items={items || []}
        month={month}
        brands={brands}
        projects={projects}
        packages={packages}
        members={members}
        statuses={ITEM_STATUSES}
        categories={categoryOptions}
        clients={(() => {
          // Derive unique clients from items — same shape the Gantt uses.
          const m = new Map();
          (items || []).forEach((it) => {
            if (it.client_id) m.set(it.client_id, { id: it.client_id, name: it.client_name || "—" });
          });
          return Array.from(m.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        })()}
      />
    </>
  );
}

function StatusChip({ s, testid }) {
  return <span className={`text-[10px] px-1.5 py-0.5 whitespace-nowrap ${STATUS_TONES[s] || "bg-muted"}`} data-testid={testid}>{STATUS_LABEL(s)}</span>;
}

function ItemPill({ it, onOpen, onOpenTask, members = [], onReassigned }) {
  const c = it.activity_type_color
    || (it.status === "published" ? "#059669"
        : it.status === "cancelled" ? "#dc2626" : "#7c3aed");
  const topic = it.linked_bucket?.topic_title;
  const btn = (
    <button onClick={() => onOpen(it)} className="w-full text-left text-[10px] px-1 py-0.5 truncate border-l-2 hover:bg-muted/60 bg-muted/30 flex items-center gap-1"
      style={{ borderColor: c }}
      data-testid={`cc-pill-${it.id}`}>
      <TypeIcon iconKey={it.activity_type_icon} color={c} size={10} />
      <span className="truncate">{it.name}</span>
      {topic && <Link2 className="w-2.5 h-2.5 text-emerald-600 shrink-0" />}
    </button>
  );
  return (
    <HoverCard openDelay={180} closeDelay={100}>
      <HoverCardTrigger asChild>{btn}</HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-[560px] p-0 rounded-none border-border"
                                data-testid={`cc-pill-hover-${it.id}`}>
        <CalendarHoverCard it={it} onOpen={onOpen} onOpenTask={onOpenTask} members={members} onReassigned={onReassigned} />
      </HoverCardContent>
    </HoverCard>
  );
}

// Iter 189 — rich hover popover with topic · status · SLA · author · thumbnail
// Iter · Feb 2026 — Widened + per-task "Open" button so PMs can jump straight
// into the task edit dialog from the hover, and each task row fits on a single
// line without wrapping.
export function CalendarHoverCard({ it, onOpen, onOpenTask, members = [], onReassigned }) {
  const b = it.linked_bucket;
  const c = it.activity_type_color || "#7c3aed";
  return (
    <div className="text-xs">
      <div className="px-3 py-2 border-b border-border flex items-center gap-2"
              style={{ background: c + "10", borderLeft: `4px solid ${c}` }}>
        <TypeIcon iconKey={it.activity_type_icon} color={c} size={14} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{it.name}</div>
          <div className="text-[10px] text-muted-foreground truncate">{it.brand_name} · {it.category}</div>
        </div>
      </div>
      {b?.preview_uri && (
        <img src={b.preview_uri} alt="" className="w-full aspect-video object-cover border-b border-border" />
      )}
      <div className="p-3 space-y-1.5">
        <div className="grid grid-cols-2 gap-1 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Planned</div>
            <div className="font-mono">{it.planned_date || "—"}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Status</div>
            <div className={
              it.status === "published" ? "text-emerald-700 font-semibold"
              : it.status === "cancelled" ? "text-rose-700 font-semibold"
              : "font-semibold"
            }>{it.status}</div>
          </div>
        </div>
        {b && (
          <>
            <div className="border-t border-border pt-1.5">
              <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold">Linked bucket topic</div>
              <div className="font-bold text-emerald-900" data-testid="cc-hover-topic-linked">{b.topic_title || "—"}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {b.format ? `${b.format} · ` : ""}{b.status || ""}
                {b.created_by_name && ` · by ${b.created_by_name}`}
              </div>
              {b.sla_due_at && (
                <div className="text-[10px] text-amber-800 mt-0.5">
                  SLA due {b.sla_due_at.slice(0, 16).replace("T", " ")}
                </div>
              )}
            </div>
          </>
        )}
        {(!b || !b.topic_title) && it.topic && (
          <div className="border-t border-border pt-1.5">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Topic</div>
            <div className="text-[11px] font-bold text-foreground line-clamp-3" data-testid="cc-hover-topic-free">{it.topic}</div>
          </div>
        )}
        <div className="flex items-center justify-between pt-1.5 border-t border-border">
          <div className="text-[10px] font-mono text-muted-foreground">
            {it.tasks_done}/{it.tasks_total} tasks
          </div>
          <button type="button" onClick={() => onOpen(it)}
                    className="text-[10px] font-bold text-primary hover:underline"
                    data-testid={`cc-hover-open-${it.id}`}>
            Open →
          </button>
        </div>
        {(it.tasks || []).length > 0 && (
          <div className="border-t border-border pt-1.5" data-testid={`cc-hover-tasks-${it.id}`}>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Tasks</div>
            <div className="space-y-0.5 max-h-40 overflow-y-auto pr-1">
              {(it.tasks || []).map((t) => (
                <div key={t.id}
                     className="text-[11px] flex items-center gap-1.5 whitespace-nowrap py-0.5"
                     data-testid={`cc-hover-task-${it.id}-${t.id}`}>
                  <span className={`w-1.5 h-1.5 shrink-0 ${
                    t.status === "done" ? "bg-emerald-500"
                      : t.status === "in_progress" ? "bg-blue-500"
                      : t.status === "blocked" ? "bg-red-500"
                      : "bg-muted-foreground"}`} />
                  {t.assignee_name && (
                    <span className="text-muted-foreground shrink-0 max-w-[110px] overflow-hidden text-ellipsis"
                          data-testid={`cc-hover-task-assignee-${it.id}-${t.id}`}>
                      {t.assignee_name}
                    </span>
                  )}
                  {t.due_date && (
                    <span className={`font-mono text-[9px] shrink-0 ${
                      t.due_date < new Date().toISOString().slice(0, 10) && t.status !== "done"
                        ? "text-rose-600 font-semibold"
                        : "text-muted-foreground"
                    }`}
                          title={`Due ${t.due_date}`}
                          data-testid={`cc-hover-task-due-${it.id}-${t.id}`}>
                      {t.due_date.slice(5)}
                    </span>
                  )}
                  <span className="flex-1 min-w-0 overflow-hidden text-ellipsis" title={t.title}>{t.title}</span>
                  {t.est_hours ? <span className="text-[9px] text-muted-foreground shrink-0">{t.est_hours}h</span> : null}
                  {members.length > 0 && (
                    <select
                      className="text-[9px] border border-border bg-background px-1 py-0 rounded-none hover:border-primary shrink-0 max-w-[110px]"
                      defaultValue={t.assignee_id || ""}
                      onChange={async (e) => {
                        const uid = e.target.value || null;
                        try {
                          await api.patch(`/tasks/${t.id}`, { assignee_id: uid });
                          const who = uid ? (members.find((m) => m.user_id === uid) || {}).name || "member" : "unassigned";
                          toast.success(`Reassigned to ${who}`);
                          onReassigned?.();
                        } catch (err) { toast.error(apiErrorMessage(err, "Reassign failed")); }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`cc-hover-reassign-${t.id}`}
                      title="Reassign this task"
                    >
                      <option value="">— unassigned —</option>
                      {members
                        .slice()
                        .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""))
                        .map((m) => (
                          <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                        ))}
                    </select>
                  )}
                  {onOpenTask && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenTask(t.id); }}
                      className="text-[9.5px] font-bold text-primary hover:underline shrink-0 px-1 py-0 border border-primary/40 hover:bg-primary/10"
                      data-testid={`cc-hover-task-open-${it.id}-${t.id}`}
                      title="Open task in edit view"
                    >
                      Open →
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ListView({ items, onOpen, onOpenActivity, onOpenTask, onChanged, members = [] }) {
  const setStatus = async (it, status) => {
    try { await api.patch(`/content-items/${it.id}`, { status }); toast.success("Updated"); onChanged?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  // Iter · Feb 2026 — Quick search + sortable columns + sticky header +
  // SN column + compact rows. Filter/sort happens client-side against
  // the already-fetched `items` array.
  const [q, setQ] = React.useState("");
  const [sortKey, setSortKey] = React.useState("planned_date");
  const [sortDir, setSortDir] = React.useState("asc");
  const setSort = (k) => {
    if (sortKey === k) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); return; }
    setSortKey(k); setSortDir("asc");
  };
  const sortArrow = (k) => sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  const visible = React.useMemo(() => {
    let list = items || [];
    const query = q.trim().toLowerCase();
    if (query) {
      list = list.filter((it) => {
        const hay = [
          it.name, it.brand_name, it.client_name, it.package_name,
          it.project_name, it.category, it.status,
          it.primary_assignee_name,
          // Feb 2026 · Include topic in search so admins can find a
          // post by what it's about, not just its deliverable name.
          it.topic, it.linked_bucket?.topic_title,
          ...(it.project_names || []),
          ...((it.tasks || []).map((t) => `${t.title || ""} ${t.assignee_name || ""}`)),
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(query);
      });
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (it) => {
      switch (sortKey) {
        case "name": return (it.name || "").toLowerCase();
        case "topic": return (it.linked_bucket?.topic_title || it.topic || "").toLowerCase();
        case "brand": return (it.brand_name || "").toLowerCase();
        case "package": return (it.package_name || "").toLowerCase();
        case "category": return (it.category || "").toLowerCase();
        case "status": return (it.status || "").toLowerCase();
        case "primary_assignee": return (it.primary_assignee_name || "").toLowerCase();
        case "done": return (it.tasks_done || 0) / Math.max(1, it.tasks_total || 1);
        case "excess": return Number(it.excess_hours) || 0;
        default: return (it.planned_date || "");
      }
    };
    return [...list].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
  }, [items, q, sortKey, sortDir]);

  // Iter · Feb 2026 — Group by (none / category / assignee) so PMs
  // can compare est-vs-actual by activity type or teammate. Grouping
  // happens AFTER filter + sort so counts and effort totals inside
  // each header reflect what's actually visible.
  const [groupBy, setGroupBy] = React.useState("none");
  const [collapsedGroups, setCollapsedGroups] = React.useState(() => new Set());
  const toggleGroup = (k) => setCollapsedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  // Iter · Feb 2026 · R258 — Freeze first N columns (0-5) via sticky
  // positioning so PMs can keep context (SN, Date, Log, Deliverable,
  // Client·Brand) visible while scrolling through the wide table.
  const [freezeCount, setFreezeCount] = React.useState(0);

  // Iter · Feb 2026 · R260 — Column-selector modal. `hiddenCols` is a
  // set of column keys the user has hidden; the header + every row
  // skip those cells and the grid template only lists visible widths.
  const [hiddenCols, setHiddenCols] = React.useState(() => new Set());
  const [colSelOpen, setColSelOpen] = React.useState(false);
  const COL_DEFS = React.useMemo(() => ([
    { key: "sn", label: "SN", width: 44, hideable: false },
    { key: "date", label: "Date", width: 56, hideable: true },
    { key: "log", label: "Log", width: 36, hideable: true },
    { key: "deliverable", label: "Deliverable", width: 260, hideable: false },
    // Feb 2026 · Dedicated Topic column — until now the topic title
    // appeared only as a thin green line under the deliverable name.
    // Admins asked for a sortable, hideable column so topics are
    // scannable at a glance across a long calendar list.
    { key: "topic", label: "Topic", width: 200, hideable: true },
    { key: "client", label: "Client · Brand", width: 150, hideable: true },
    { key: "package", label: "Package", width: 170, hideable: true },
    { key: "project", label: "Project", width: 140, hideable: true },
    { key: "tasks", label: "Tasks", width: 300, hideable: true },
    { key: "category", label: "Category", width: 100, hideable: true },
    { key: "status", label: "Status", width: 120, hideable: true },
    // R268uuu · Feb 2026 — Primary assignee column, shown right after
    // Status so ownership is visible next to state. Linked-task
    // assignees stay inside the Tasks column (existing behavior).
    { key: "primary_assignee", label: "Primary", width: 120, hideable: true },
    { key: "done", label: "Done", width: 60, hideable: true },
    { key: "effort", label: "Est · Actual · Excess", width: 130, hideable: true },
    { key: "collab", label: "Collab", width: 80, hideable: true },
  ]), []);
  const isHidden = (k) => hiddenCols.has(k);
  const toggleCol = (k) => setHiddenCols((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const visibleCols = React.useMemo(
    () => COL_DEFS.filter((c) => !isHidden(c.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [COL_DEFS, hiddenCols],
  );
  const COLS = visibleCols.map((c) => `${c.width}px`).join(" ");
  const minTableWidth = visibleCols.reduce((s, c) => s + c.width, 0) + (visibleCols.length - 1) * 8 + 24;

  // Cumulative left offsets for the currently visible columns
  // accounting for the parent grid's `gap-2` (8px). Only the first
  // `freezeCount` visible columns get sticky treatment.
  const FREEZE_OFFSETS = React.useMemo(() => {
    const offsets = {};
    let cursor = 0;
    visibleCols.forEach((c) => {
      offsets[c.key] = cursor;
      cursor += c.width + 8; // 8px = gap-2
    });
    return offsets;
  }, [visibleCols]);
  const visIndex = (key) => visibleCols.findIndex((c) => c.key === key);
  const stickyClass = (key) => {
    const idx = visIndex(key);
    return idx > -1 && idx < freezeCount ? "sticky z-[8] bg-background" : "";
  };
  const stickyClassHeader = (key) => {
    const idx = visIndex(key);
    return idx > -1 && idx < freezeCount ? "sticky z-[9] bg-muted" : "";
  };
  const stickyStyle = (key) => {
    const idx = visIndex(key);
    if (idx < 0 || idx >= freezeCount) return undefined;
    return {
      left: `${FREEZE_OFFSETS[key]}px`,
      boxShadow: idx === freezeCount - 1 ? "2px 0 4px -1px rgba(0,0,0,0.08)" : undefined,
    };
  };

  if (!items) {
    // Skeleton rows while the first month load is in flight.
    return (
      <div className="space-y-2" data-testid="cc-list-skeleton">
        <div className="grid gap-2 px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold border-b border-border"
             style={{ gridTemplateColumns: "260px 1fr 1fr 1fr" }}>
          <span>Deliverable</span><span>Client · Brand</span><span>Owner</span><span>Due</span>
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i}
                className="grid gap-2 px-3 py-2 items-center border-b border-border/60"
                style={{ gridTemplateColumns: "260px 1fr 1fr 1fr", opacity: 1 - (i * 0.09) }}>
            <div className="h-3 bg-muted/70 animate-pulse" />
            <div className="h-3 bg-muted/50 animate-pulse w-3/4" />
            <div className="h-3 bg-muted/50 animate-pulse w-1/2" />
            <div className="h-3 bg-muted/50 animate-pulse w-2/3" />
          </div>
        ))}
      </div>
    );
  }
  if (items.length === 0) return <EmptyHint />;

  // Build groups. For assignee/project buckets, one item can span
  // multiple values → put the item under EVERY matching bucket. For
  // scalar dims (client, brand, package, primary, status, category),
  // each item belongs to exactly one bucket.
  const groups = (() => {
    if (groupBy === "none") return [{ key: "_all", label: "", items: visible }];
    const map = new Map();
    const push = (k, label, it) => {
      if (!map.has(k)) map.set(k, { key: k, label, items: [] });
      map.get(k).items.push(it);
    };
    // R268~2 · Feb 2026 — Extended dims. Client/Brand/Package/Project/
    // Primary/Status all pivot the list without a re-fetch — grouping
    // is a pure client-side transform over the already-fetched rows.
    if (groupBy === "category") {
      for (const it of visible) { const c = it.category || "Uncategorised"; push(c, c, it); }
    } else if (groupBy === "assignee") {
      for (const it of visible) {
        const seen = new Set();
        for (const t of (it.tasks || [])) {
          const name = t.assignee_name || "Unassigned";
          if (seen.has(name)) continue;
          seen.add(name);
          push(name, name, it);
        }
        if ((it.tasks || []).length === 0) push("Unassigned", "Unassigned", it);
      }
    } else if (groupBy === "primary_assignee") {
      for (const it of visible) {
        const name = it.primary_assignee_name || "Unassigned";
        push(name, name, it);
      }
    } else if (groupBy === "client") {
      for (const it of visible) {
        const name = it.client_name || "No client";
        push(name, name, it);
      }
    } else if (groupBy === "brand") {
      for (const it of visible) {
        const name = it.brand_name || "No brand";
        push(name, name, it);
      }
    } else if (groupBy === "package") {
      for (const it of visible) {
        const name = it.package_name || "No package";
        push(name, name, it);
      }
    } else if (groupBy === "project") {
      for (const it of visible) {
        const names = Array.isArray(it.project_names) && it.project_names.length > 0
          ? it.project_names
          : ["No project"];
        const seen = new Set();
        for (const n of names) {
          if (seen.has(n)) continue;
          seen.add(n);
          push(n, n, it);
        }
      }
    } else if (groupBy === "status") {
      for (const it of visible) {
        const raw = it.status || "unscheduled";
        const label = STATUS_LABEL(raw);
        push(raw, label, it);
      }
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  })();
  const rollup = (list) => {
    const est = list.reduce((n, x) => n + (Number(x.est_hours_total) || 0), 0);
    const act = list.reduce((n, x) => n + (Number(x.actual_hours_total) || 0), 0);
    return { est: est.toFixed(1), act: act.toFixed(1), excess: Math.max(0, act - est).toFixed(1) };
  };
  return (
    <div className="border border-border overflow-x-auto" data-testid="cc-list">
      {/* Quick search bar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/20 border-b border-border">
        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search deliverable, brand, package, task…"
          className="h-7 flex-1 min-w-0 text-xs px-2 border border-border rounded-none bg-white"
          data-testid="cc-list-search"
        />
        {q && (
          <button onClick={() => setQ("")} className="text-xs text-muted-foreground hover:text-foreground" title="Clear" data-testid="cc-list-search-clear">×</button>
        )}
        {/* Iter · Feb 2026 — Group by dropdown lets PMs pivot the list
            by activity category or task assignee to compare estimated
            vs actual effort per bucket without leaving the calendar. */}
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Group</span>
          <Select value={groupBy} onValueChange={(v) => { setGroupBy(v); setCollapsedGroups(new Set()); }}>
            <SelectTrigger className="h-7 w-[170px] rounded-none text-[11px] px-2" data-testid="cc-list-groupby">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="brand">Brand</SelectItem>
              <SelectItem value="package">Package</SelectItem>
              <SelectItem value="project">Project</SelectItem>
              <SelectItem value="primary_assignee">Deliverable owner</SelectItem>
              <SelectItem value="assignee">Task assignee</SelectItem>
              <SelectItem value="status">Deliverable status</SelectItem>
              <SelectItem value="category">Category</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1 shrink-0" title="Keep the first N columns visible while scrolling horizontally">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Freeze</span>
          <Select value={String(freezeCount)} onValueChange={(v) => setFreezeCount(Number(v))}>
            <SelectTrigger className="h-7 w-[80px] rounded-none text-[11px] px-2" data-testid="cc-list-freezecols">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">None</SelectItem>
              <SelectItem value="1">1 col</SelectItem>
              <SelectItem value="2">2 cols</SelectItem>
              <SelectItem value="3">3 cols</SelectItem>
              <SelectItem value="4">4 cols</SelectItem>
              <SelectItem value="5">5 cols</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="relative shrink-0">
          <Button variant="outline" size="sm" className="h-7 rounded-none text-[11px] px-2"
                  onClick={() => setColSelOpen((v) => !v)}
                  data-testid="cc-list-colsel-btn">
            Columns {hiddenCols.size > 0 ? `(${COL_DEFS.length - hiddenCols.size}/${COL_DEFS.length})` : ""}
          </Button>
          {colSelOpen && (
            <div className="absolute right-0 top-full mt-1 z-30 bg-background border border-border shadow-lg p-2 min-w-[220px]"
                 data-testid="cc-list-colsel-panel">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Show columns</span>
                <button className="text-[10px] text-primary hover:underline"
                        onClick={() => setHiddenCols(new Set())}
                        data-testid="cc-list-colsel-all">All</button>
              </div>
              {COL_DEFS.map((c) => (
                <label key={c.key}
                       className={`flex items-center gap-2 py-0.5 text-[11px] cursor-pointer ${!c.hideable ? "opacity-50 cursor-not-allowed" : "hover:bg-muted/40"}`}
                       data-testid={`cc-list-colsel-${c.key}`}>
                  <input type="checkbox"
                         checked={!isHidden(c.key)}
                         disabled={!c.hideable}
                         onChange={() => c.hideable && toggleCol(c.key)} />
                  {c.label}
                  {!c.hideable && <span className="text-[9px] text-muted-foreground ml-auto">required</span>}
                </label>
              ))}
              <div className="mt-2 pt-2 border-t border-border">
                <button className="text-[10px] text-muted-foreground hover:text-primary w-full text-right"
                        onClick={() => setColSelOpen(false)}
                        data-testid="cc-list-colsel-close">Close</button>
              </div>
            </div>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground">{visible.length} of {items.length}</div>
      </div>
      {/* Sticky header */}
      <div className="grid gap-2 px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold sticky top-0 z-10 border-b border-border"
           style={{ gridTemplateColumns: COLS, minWidth: `${minTableWidth}px` }}>
        {!isHidden("sn") && <div className={stickyClassHeader("sn")} style={stickyStyle("sn")}>SN</div>}
        {!isHidden("date") && <button className={`text-left hover:text-foreground ${stickyClassHeader("date")}`} style={stickyStyle("date")} onClick={() => setSort("planned_date")} data-testid="cc-list-sort-date">Date{sortArrow("planned_date")}</button>}
        {!isHidden("log") && <div className={`text-center ${stickyClassHeader("log")}`} style={stickyStyle("log")} title="Open the Activity + Comments log for this deliverable">Log</div>}
        {!isHidden("deliverable") && <button className={`text-left hover:text-foreground ${stickyClassHeader("deliverable")}`} style={stickyStyle("deliverable")} onClick={() => setSort("name")} data-testid="cc-list-sort-name">Deliverable{sortArrow("name")}</button>}
        {!isHidden("topic") && <button className="text-left hover:text-foreground" onClick={() => setSort("topic")} data-testid="cc-list-sort-topic">Topic{sortArrow("topic")}</button>}
        {!isHidden("client") && <button className={`text-left hover:text-foreground ${stickyClassHeader("client")}`} style={stickyStyle("client")} onClick={() => setSort("brand")} data-testid="cc-list-sort-brand">Client · Brand{sortArrow("brand")}</button>}
        {!isHidden("package") && <button className="text-left hover:text-foreground" onClick={() => setSort("package")} data-testid="cc-list-sort-package">Package{sortArrow("package")}</button>}
        {!isHidden("project") && <div>Project</div>}
        {!isHidden("tasks") && <div>Tasks</div>}
        {!isHidden("category") && <button className="text-left hover:text-foreground" onClick={() => setSort("category")} data-testid="cc-list-sort-category">Category{sortArrow("category")}</button>}
        {!isHidden("status") && <button className="text-left hover:text-foreground" onClick={() => setSort("status")} data-testid="cc-list-sort-status">Status{sortArrow("status")}</button>}
        {!isHidden("primary_assignee") && <button className="text-left hover:text-foreground" onClick={() => setSort("primary_assignee")} data-testid="cc-list-sort-primary">Primary{sortArrow("primary_assignee")}</button>}
        {!isHidden("done") && <button className="text-right hover:text-foreground" onClick={() => setSort("done")} data-testid="cc-list-sort-done">Done{sortArrow("done")}</button>}
        {!isHidden("effort") && <button className="text-right hover:text-foreground" onClick={() => setSort("excess")} data-testid="cc-list-sort-effort">Est · Actual · Excess{sortArrow("excess")}</button>}
        {!isHidden("collab") && <div className="text-right">Collab</div>}
      </div>
      {(() => {
        let __sn = 0;
        return groups.map((g) => {
          const __collapsed = collapsedGroups.has(g.key);
          const __rl = rollup(g.items);
          const __gkey = String(g.key).replace(/[^a-z0-9_-]+/gi, "-");
          return (
            <React.Fragment key={g.key}>
              {groupBy !== "none" && (
                <div className="grid gap-2 px-3 py-1.5 items-center bg-muted/60 border-y border-border min-w-[1760px] cursor-pointer hover:bg-muted select-none"
                     style={{ gridTemplateColumns: "24px minmax(260px, 1fr) auto" }}
                     onClick={() => toggleGroup(g.key)}
                     data-testid={`cc-group-${__gkey}`}>
                  <span className="text-muted-foreground text-[10px]" aria-hidden="true">{__collapsed ? "▶" : "▼"}</span>
                  <div className="text-[11px] font-semibold text-foreground truncate">
                    {g.label}
                    <span className="ml-2 text-[10px] text-muted-foreground font-normal" data-testid={`cc-group-count-${__gkey}`}>{g.items.length} item{g.items.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="text-right text-[10px] font-mono" data-testid={`cc-group-effort-${__gkey}`}>
                    <span className="text-muted-foreground">Est {__rl.est}h</span>
                    <span className="mx-1 text-muted-foreground/60">·</span>
                    <span className={Number(__rl.excess) > 0 ? "text-rose-600 font-semibold" : "text-emerald-700"}>Actual {__rl.act}h</span>
                    {Number(__rl.excess) > 0 && (<><span className="mx-1 text-muted-foreground/60">·</span><span className="text-rose-600 font-semibold">+{__rl.excess}h</span></>)}
                  </div>
                </div>
              )}
              {!__collapsed && g.items.map((it) => {
                const idx = __sn++;
                return (
        <div key={`${g.key}-${it.id}`}
              className="grid gap-2 px-3 py-1 items-start text-xs hover:bg-muted/30 border-b border-border/60"
              style={{ gridTemplateColumns: COLS, minWidth: `${minTableWidth}px` }}
              data-testid={`cc-row-${it.id}`}>
          {!isHidden("sn") && <div className={`font-mono text-[10px] text-muted-foreground ${stickyClass("sn")}`} style={stickyStyle("sn")} data-testid={`cc-row-sn-${it.id}`}>{idx + 1}</div>}
          {!isHidden("date") && <div className={`font-mono text-[11px] ${stickyClass("date")}`} style={stickyStyle("date")}>{(it.planned_date || "").slice(5)}</div>}
          {!isHidden("log") && <div className={stickyClass("log")} style={stickyStyle("log")}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenActivity ? onOpenActivity(it) : onOpen(it); }}
            className="w-6 h-6 mx-auto flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 rounded"
            title="Open Activity + Comments"
            data-testid={`cc-row-activity-${it.id}`}
          ><MessageSquare className="w-3.5 h-3.5" /></button>
          </div>}
          {!isHidden("deliverable") && <div className={`min-w-0 ${stickyClass("deliverable")}`} style={stickyStyle("deliverable")}>
          <HoverCard openDelay={200} closeDelay={80}>
            <HoverCardTrigger asChild>
              <button className="text-left hover:text-primary min-w-0 w-full" onClick={() => onOpen(it)} data-testid={`cc-row-open-${it.id}`}>
                <div className="flex items-start gap-1.5 min-w-0">
                  <TypeIcon iconKey={it.activity_type_icon} color={it.activity_type_color} size={12} />
                  {/* Iter · Feb 2026 — Wrap long deliverable titles so they
                      don't overflow into the next column. break-words lets
                      the browser break mid-word for very long slugs. */}
                  <span className="font-medium break-words whitespace-normal leading-tight">{it.name}</span>
                </div>
                {it.linked_bucket?.topic_title && (
                  <div className="text-[10px] text-emerald-700 break-words whitespace-normal mt-0.5 flex items-start gap-0.5"
                         title={`Linked topic: ${it.linked_bucket.topic_title}`}
                         data-testid={`cc-row-topic-${it.id}`}>
                    <Link2 className="w-2.5 h-2.5 shrink-0 mt-0.5" />
                    <span className="break-words whitespace-normal">{it.linked_bucket.topic_title}</span>
                  </div>
                )}
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="right" align="start" className="w-[560px] p-0 rounded-none" data-testid={`cc-hovercard-${it.id}`}>
              <CalendarHoverCard it={it} onOpen={onOpen} onOpenTask={onOpenTask} members={members} onReassigned={onChanged} />
            </HoverCardContent>
          </HoverCard>
          </div>}
          {/* Feb 2026 · Dedicated Topic column cell. Prefers the linked
              bucket's title; falls back to the deliverable's flat
              `topic` field. The inline green topic line under the
              deliverable name stays — it's useful when the Topic
              column is hidden. */}
          {!isHidden("topic") && <div className="min-w-0 text-[11px]" data-testid={`cc-row-topic-col-${it.id}`}>
            {(() => {
              const t = it.linked_bucket?.topic_title || it.topic;
              if (!t) return <span className="text-muted-foreground">—</span>;
              const linked = Boolean(it.linked_bucket?.topic_title);
              return (
                <div className={`flex items-start gap-1 ${linked ? "text-emerald-700" : "text-foreground"}`}
                      title={t}>
                  {linked && <Link2 className="w-2.5 h-2.5 shrink-0 mt-0.5" />}
                  <span className="break-words whitespace-normal leading-snug line-clamp-3">{t}</span>
                </div>
              );
            })()}
          </div>}
          {!isHidden("client") && <div className={`min-w-0 text-[11px] ${stickyClass("client")}`} style={stickyStyle("client")} data-testid={`cc-row-client-${it.id}`}>
            {it.client_name && <div className="truncate font-medium text-indigo-700">{it.client_name}</div>}
            <div className="truncate text-muted-foreground">{it.brand_name || "—"}</div>
          </div>}
          {!isHidden("package") && <div className="min-w-0 text-[11px] flex items-center gap-1" data-testid={`cc-row-package-${it.id}`}>
            {it.package_name ? (
              <>
                <PackageHoverPreview pid={it.package_id} disabled={!it.package_id}>
                  <span className="chip text-[10px] bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30 truncate cursor-help">{it.package_name}</span>
                </PackageHoverPreview>
                {/* Iter · Feb 2026 — open-in-new-tab shortcut so PMs can
                    inspect a package without leaving the calendar. */}
                {it.package_id && (
                  <a
                    href={`/app/packages/${it.package_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-700 hover:text-purple-900 shrink-0"
                    title="Open package in new tab"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`cc-row-package-open-${it.id}`}
                  ><ExternalLink className="w-3 h-3" /></a>
                )}
              </>
            ) : <span className="text-muted-foreground">—</span>}
          </div>}
          {!isHidden("project") && <div className="min-w-0 text-[11px]" data-testid={`cc-row-project-${it.id}`}>
            {it.project_name ? (
              <span className="chip text-[10px] bg-primary/10 text-primary border border-primary/30 truncate max-w-full" title={it.project_name}>{it.project_name}</span>
            ) : (it.project_names || []).length > 0 ? (
              <div className="flex flex-wrap gap-0.5">
                {(it.project_names || []).map((pn, i) => (
                  <span key={i} className="chip text-[9px] bg-primary/10 text-primary border border-primary/30">{pn}</span>
                ))}
              </div>
            ) : <span className="text-muted-foreground">—</span>}
          </div>}
          {!isHidden("tasks") && <div className="min-w-0" data-testid={`cc-row-tasks-${it.id}`}>
            {(it.tasks || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}
            {(it.tasks || []).map((t) => (
              <div key={t.id} className="text-[11px] flex items-center gap-1 w-full">
                {/* Iter · Feb 2026 — Inline status color dropdown, like
                    Planning Board's dot-menu. Colour reflects current
                    status; click swaps status without opening the
                    task detail popup. */}
                <TaskStatusDot task={t} onChanged={onChanged} />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenTask?.(t.id); }}
                  className="min-w-0 flex-1 text-left hover:text-primary hover:underline flex items-center gap-1"
                  title={`Open task: ${t.title}${t.assignee_name ? ` — ${t.assignee_name}` : ""}${t.due_date ? ` · due ${t.due_date}` : ""}`}
                  data-testid={`cc-row-task-${it.id}-${t.id}`}>
                  {t.due_date && (
                    <span className={`font-mono shrink-0 text-[10px] ${
                      t.due_date < new Date().toISOString().slice(0, 10) && t.status !== "done"
                        ? "text-rose-600 font-semibold"
                        : "text-muted-foreground"
                    }`}
                          title={`Due ${t.due_date}`}
                          data-testid={`cc-row-task-due-${it.id}-${t.id}`}>{t.due_date.slice(5)}</span>
                  )}
                  <span className="truncate min-w-0">{t.title}</span>
                  {/* Iter · Feb 2026 — assignee name shown in parens
                      trailing the task title so PMs can spot who owns
                      what at a glance. */}
                  {t.assignee_name && (
                    <span className="text-muted-foreground shrink-0 text-[10px]"
                          data-testid={`cc-row-task-assignee-${it.id}-${t.id}`}>({t.assignee_name})</span>
                  )}
                </button>
              </div>
            ))}
            {/* Feb 2026 · Quick task-create linked to this deliverable.
                Sanctioned entry point when the parent project is
                package-locked (regular /workspaces/{ws}/tasks 409s in
                that case). Uses window.prompt for the title so we don't
                pull in a full dialog for a two-tap flow. */}
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                const title = window.prompt("New task for this deliverable — title?");
                if (!title?.trim()) return;
                try {
                  await api.post(`/content-items/${it.id}/linked-tasks`, {
                    mode: "new", title: title.trim(),
                  });
                  toast.success("Task linked to deliverable");
                  onChanged?.();
                } catch (err) {
                  toast.error(apiErrorMessage(err, "Couldn't add task"));
                }
              }}
              className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground hover:text-primary mt-0.5"
              data-testid={`cc-row-add-task-${it.id}`}
              title="Add a task linked to this deliverable">
              + Task
            </button>
          </div>}
          {!isHidden("category") && <div>
            <Badge variant="outline" className="rounded-none text-[10px]"
                     style={it.activity_type_color ? {
                       borderColor: it.activity_type_color + "66",
                       color: it.activity_type_color,
                       background: it.activity_type_color + "10",
                     } : {}}>
              {it.category}
            </Badge>
          </div>}
          {!isHidden("status") && <div>
            <Select value={it.status} onValueChange={(v) => setStatus(it, v)}>
              <SelectTrigger className="h-6 rounded-none text-[11px] px-1.5" data-testid={`cc-row-status-${it.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{ITEM_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          {/* R268uuu · Feb 2026 — Primary assignee cell. Read-only
              here (edited from the package page + item drawer); the
              badge stays subtle so it doesn't compete with the
              interactive Status pill next to it. */}
          {!isHidden("primary_assignee") && <div className="text-[11px] truncate" title={it.primary_assignee_name || "Unassigned"}
                                                    data-testid={`cc-row-primary-${it.id}`}>
            {it.primary_assignee_name ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-border bg-muted/40 rounded-sm">
                <UserIcon className="w-3 h-3 text-muted-foreground" />
                <span className="truncate max-w-[90px]">{it.primary_assignee_name}</span>
              </span>
            ) : (
              <span className="text-muted-foreground/50">—</span>
            )}
          </div>}
          {!isHidden("done") && <div className="text-right text-[11px] font-mono">{it.tasks_done}/{it.tasks_total}</div>}
          {/* Iter · Feb 2026 — Est · Actual · Excess effort roll-up per
              deliverable so PMs can spot overruns at a glance. Colour
              flips to rose when actual > est. */}
          {!isHidden("effort") && <div className="text-right text-[10px] font-mono" data-testid={`cc-row-effort-${it.id}`}>
            {(() => {
              const est = Number(it.est_hours_total) || 0;
              const act = Number(it.actual_hours_total) || 0;
              const excess = Number(it.excess_hours) || 0;
              if (!est && !act) return <span className="text-muted-foreground">—</span>;
              return (
                <span title={`Estimated ${est}h · Actual ${act}h · Excess ${excess}h`}>
                  <span className="text-muted-foreground">{est.toFixed(1)}h</span>
                  <span className="mx-0.5 text-muted-foreground/60">·</span>
                  <span className={excess > 0 ? "text-rose-600 font-semibold" : "text-emerald-700"}>{act.toFixed(1)}h</span>
                  {excess > 0 && (<><span className="mx-0.5 text-muted-foreground/60">·</span><span className="text-rose-600 font-semibold">+{excess.toFixed(1)}h</span></>)}
                </span>
              );
            })()}
          </div>}
          {!isHidden("collab") && <div className="text-right text-[11px] flex items-center justify-end gap-2" data-testid={`cc-row-collab-${it.id}`}>
            {(it.comment_count || 0) > 0 && (
              <span className="inline-flex items-center gap-0.5 text-primary" title={`${it.comment_count} comment(s)`}>
                💬 {it.comment_count}
              </span>
            )}
            {(it.activity_count || 0) > 0 && (
              <span className="inline-flex items-center gap-0.5 text-muted-foreground" title={`${it.activity_count} activity event(s)`}>
                ⚡ {it.activity_count}
              </span>
            )}
            {!(it.comment_count || 0) && !(it.activity_count || 0) && (
              <span className="text-muted-foreground">—</span>
            )}
          </div>}
        </div>
                );
              })}
            </React.Fragment>
          );
        });
      })()}
    </div>
  );
}

// Iter · Feb 2026 — Small colored status dot with an inline dropdown so
// PMs can flip a task's status straight from the content calendar list
// without opening the task detail. Mirrors the Planning Board's dot.
const TASK_STATUS_MAP = {
  todo:        { color: "bg-neutral-300",  label: "To do" },
  in_progress: { color: "bg-blue-500",    label: "In progress" },
  review:      { color: "bg-violet-500",  label: "Review" },
  blocked:     { color: "bg-rose-500",    label: "Blocked" },
  done:        { color: "bg-emerald-500", label: "Done" },
  cancelled:   { color: "bg-neutral-400", label: "Cancelled" },
};
function TaskStatusDot({ task, onChanged }) {
  const meta = TASK_STATUS_MAP[task.status] || TASK_STATUS_MAP.todo;
  const setStatus = async (v) => {
    if (v === task.status) return;
    try {
      await api.patch(`/tasks/${task.id}`, { status: v });
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  return (
    <Select value={task.status || "todo"} onValueChange={setStatus}>
      <SelectTrigger
        className="w-4 h-4 p-0 border-0 rounded-none shrink-0 focus:ring-0 focus:ring-offset-0 [&>svg]:hidden bg-transparent hover:opacity-80"
        title={`Status: ${meta.label} — click to change`}
        data-testid={`cc-row-task-statusdot-${task.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={`w-2 h-2 ${meta.color}`} aria-hidden="true" />
      </SelectTrigger>
      <SelectContent onClick={(e) => e.stopPropagation()}>
        {Object.entries(TASK_STATUS_MAP).map(([k, v]) => (
          <SelectItem key={k} value={k}>
            <span className="inline-flex items-center gap-2">
              <span className={`w-2 h-2 ${v.color}`} />
              {v.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MonthView({ items, year, month, onOpen, onOpenTask, onReloaded, members = [] }) {
  const [dragId, setDragId] = useState(null);
  const cells = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const days = new Date(year, month, 0).getDate();
    const lead = (first.getDay() + 6) % 7;
    const byDay = {};
    (items || []).forEach((it) => { const d = parseInt((it.planned_date || "").slice(8), 10); (byDay[d] = byDay[d] || []).push(it); });
    const out = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= days; d++) out.push({ d, items: byDay[d] || [] });
    return out;
  }, [items, year, month]);
  const drop = async (targetDay) => {
    if (!dragId) return;
    const it = (items || []).find((x) => x.id === dragId);
    if (!it) return;
    const targetDate = `${year}-${String(month).padStart(2, "0")}-${String(targetDay).padStart(2, "0")}`;
    if (it.planned_date === targetDate) { setDragId(null); return; }
    try {
      await api.patch(`/content-items/${it.id}/reschedule`, { planned_date: targetDate });
      toast.success(`Moved to ${targetDate}`);
      onReloaded?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reschedule failed"));
    } finally {
      setDragId(null);
    }
  };
  return (
    <div data-testid="cc-month-grid">
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 border-t border-l border-border">
        {cells.map((c, i) => (
          <div key={i} className={`min-h-[92px] border-r border-b border-border p-1 space-y-0.5 ${c && dragId ? "hover:bg-primary/5" : ""}`}
               onDragOver={(e) => { if (c && dragId) e.preventDefault(); }}
               onDrop={() => c && drop(c.d)}
               data-testid={c ? `cc-month-cell-${c.d}` : undefined}>
            {c && <>
              <div className="text-[10px] font-mono text-muted-foreground">{c.d}</div>
              {c.items.map((it) => (
                <div key={it.id}
                     draggable
                     onDragStart={() => setDragId(it.id)}
                     onDragEnd={() => setDragId(null)}
                     className={`${dragId === it.id ? "opacity-40" : ""}`}>
                  <ItemPill it={it} onOpen={onOpen} onOpenTask={onOpenTask} members={members} onReassigned={onReloaded} />
                </div>
              ))}
            </>}
          </div>
        ))}
      </div>
    </div>
  );
}

function WeekView({ items, year, month, onOpen, onOpenTask, members = [], onReloaded }) {
  const [weekIdx, setWeekIdx] = useState(0);
  const weeks = useMemo(() => {
    const days = new Date(year, month, 0).getDate();
    const out = [];
    let w = [];
    for (let d = 1; d <= days; d++) {
      w.push(d);
      if (new Date(year, month - 1, d).getDay() === 0 || d === days) { out.push(w); w = []; }
    }
    return out;
  }, [year, month]);
  const week = weeks[Math.min(weekIdx, weeks.length - 1)] || [];
  const byDay = {};
  (items || []).forEach((it) => { const d = parseInt((it.planned_date || "").slice(8), 10); (byDay[d] = byDay[d] || []).push(it); });
  return (
    <div data-testid="cc-week-view">
      <div className="flex items-center gap-2 mb-2">
        <Button variant="outline" size="sm" className="rounded-none h-7" onClick={() => setWeekIdx((i) => Math.max(0, i - 1))} data-testid="cc-week-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
        <span className="text-xs font-semibold">Week {Math.min(weekIdx, weeks.length - 1) + 1} of {weeks.length}</span>
        <Button variant="outline" size="sm" className="rounded-none h-7" onClick={() => setWeekIdx((i) => Math.min(weeks.length - 1, i + 1))} data-testid="cc-week-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
      </div>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${week.length || 1}, minmax(0,1fr))` }}>
        {week.map((d) => (
          <div key={d} className="border border-border min-h-[140px]">
            <div className="px-2 py-1 bg-muted/40 text-[10px] font-mono border-b border-border">{new Date(year, month - 1, d).toLocaleDateString("en", { weekday: "short", day: "numeric" })}</div>
            <div className="p-1 space-y-1">{(byDay[d] || []).map((it) => <ItemPill key={it.id} it={it} onOpen={onOpen} onOpenTask={onOpenTask} members={members} onReassigned={onReloaded} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DayView({ items, year, month, onOpen, onOpenTask, members = [], onReloaded }) {
  const today = new Date();
  const [day, setDay] = useState(today.getFullYear() === year && today.getMonth() + 1 === month ? today.getDate() : 1);
  const days = new Date(year, month, 0).getDate();
  const list = (items || []).filter((it) => parseInt((it.planned_date || "").slice(8), 10) === day);
  return (
    <div data-testid="cc-day-view">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="outline" size="sm" className="rounded-none h-7" onClick={() => setDay((d) => Math.max(1, d - 1))} data-testid="cc-day-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
        <span className="text-sm font-semibold w-48 text-center">{new Date(year, month - 1, day).toLocaleDateString("en", { weekday: "long", day: "numeric", month: "long" })}</span>
        <Button variant="outline" size="sm" className="rounded-none h-7" onClick={() => setDay((d) => Math.min(days, d + 1))} data-testid="cc-day-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
      </div>
      {list.length === 0 ? <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center">Nothing planned this day.</div>
        : <div className="space-y-2 max-w-2xl">{list.map((it) => (
          <HoverCard key={it.id} openDelay={200} closeDelay={80}>
            <HoverCardTrigger asChild>
              <button onClick={() => onOpen(it)} className="w-full text-left border-l-4 border border-border p-3 hover:border-primary flex items-center gap-3"
                        style={it.activity_type_color ? { borderLeftColor: it.activity_type_color } : {}}
                        data-testid={`cc-day-item-${it.id}`}>
                <TypeIcon iconKey={it.activity_type_icon} color={it.activity_type_color} size={16} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{it.name}</div>
                  <div className="text-xs text-muted-foreground">{it.brand_name} · {it.category}</div>
                  {it.linked_bucket?.topic_title ? (
                    <div className="text-[10px] text-emerald-700 truncate mt-0.5 flex items-center gap-0.5"
                           title={`Topic: ${it.linked_bucket.topic_title}`}>
                      <Link2 className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{it.linked_bucket.topic_title}</span>
                    </div>
                  ) : it.topic ? (
                    // Feb 2026 · Flat `topic` fallback so day-view
                    // items show the topic even without a linked bucket.
                    <div className="text-[10px] text-muted-foreground italic mt-0.5 truncate"
                           title={`Topic: ${it.topic}`}>
                      {it.topic}
                    </div>
                  ) : null}
                </div>
                <StatusChip s={it.status} />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="right" align="start" className="w-[560px] p-0 rounded-none" data-testid={`cc-day-hover-${it.id}`}>
              <CalendarHoverCard it={it} onOpen={onOpen} onOpenTask={onOpenTask} members={members} onReassigned={onReloaded} />
            </HoverCardContent>
          </HoverCard>))}</div>}
    </div>
  );
}

function FeedView({ items, onOpen }) {
  if (!items) return null;
  if (items.length === 0) return <EmptyHint />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="cc-feed-view">
      {items.map((it) => (
        <button key={it.id} onClick={() => onOpen(it)}
                  className="border border-border text-left hover:border-primary overflow-hidden"
                  style={it.activity_type_color ? { borderLeftWidth: 4, borderLeftColor: it.activity_type_color } : {}}
                  data-testid={`cc-feed-card-${it.id}`}>
          {it.linked_bucket?.preview_uri && (
            <img src={it.linked_bucket.preview_uri} alt=""
                    className="w-full aspect-video object-cover border-b border-border" />
          )}
          <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-2">
            <TypeIcon iconKey={it.activity_type_icon} color={it.activity_type_color} size={13} />
            <span className="text-xs font-semibold truncate flex-1">{it.brand_name}</span>
            <span className="text-[10px] font-mono text-muted-foreground">{it.planned_date}</span>
          </div>
          <div className="p-3">
            <div className="font-semibold text-sm">{it.name}</div>
            {it.linked_bucket?.topic_title && (
              <div className="text-[11px] text-emerald-700 mt-1 truncate flex items-center gap-1"
                     title={`Topic: ${it.linked_bucket.topic_title}`}>
                <Link2 className="w-3 h-3 shrink-0" />
                <span className="truncate">{it.linked_bucket.topic_title}</span>
              </div>
            )}
            {it.topic && !it.linked_bucket?.topic_title && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{it.topic}</div>
            )}
            <div className="mt-2 flex items-center gap-2">
              <StatusChip s={it.status} />
              <Badge variant="outline" className="rounded-none text-[10px]"
                       style={it.activity_type_color ? {
                         borderColor: it.activity_type_color + "66",
                         color: it.activity_type_color,
                         background: it.activity_type_color + "10",
                       } : {}}>
                {it.category}
              </Badge>
              <span className="text-[10px] font-mono text-muted-foreground ml-auto">{it.tasks_done}/{it.tasks_total} tasks</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function UpcomingView({ wsId, onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    const from = new Date().toISOString().slice(0, 10);
    const to = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
    api.get(`/workspaces/${wsId}/content-calendar?from_=${from}&to=${to}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [wsId]);
  if (!rows) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const groups = {};
  rows.forEach((it) => { (groups[it.planned_date] = groups[it.planned_date] || []).push(it); });
  const keys = Object.keys(groups).sort();
  if (keys.length === 0) return <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center" data-testid="cc-upcoming-empty">Nothing due in the next 14 days.</div>;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="cc-upcoming-view">
      {keys.map((d) => (
        <div key={d}>
          <div className="label-eyebrow text-[10px] mb-1.5">{new Date(d).toLocaleDateString("en", { weekday: "long", day: "numeric", month: "long" })}</div>
          <div className="border border-border divide-y divide-border">
            {groups[d].map((it) => {
              const topic = it.linked_bucket?.topic_title || it.topic;
              return (
                <button key={it.id} onClick={() => onOpen(it)} className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-muted/30" data-testid={`cc-upcoming-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.name}</div>
                    {topic && (
                      <div className={`text-[10px] truncate flex items-center gap-1 ${it.linked_bucket?.topic_title ? "text-emerald-700" : "text-muted-foreground italic"}`}
                           title={`Topic: ${topic}`}>
                        {it.linked_bucket?.topic_title && <Link2 className="w-2.5 h-2.5 shrink-0" />}
                        <span className="truncate">{topic}</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground truncate">{it.brand_name}</div>
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground">{it.tasks_done}/{it.tasks_total}</span>
                  <StatusChip s={it.status} />
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="border border-dashed border-border p-10 text-center" data-testid="cc-empty">
      <CalendarDays className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
      <div className="font-semibold">No deliverables this month</div>
      <p className="text-sm text-muted-foreground mt-1">Use &ldquo;Generate month&rdquo; to expand active packages into deliverable occurrences &mdash; you&apos;ll get a preview first.</p>
    </div>
  );
}

function ComparePackagePicker({ packages, selectedId, onSelect, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return packages;
    return packages.filter((p) => (p.name || "").toLowerCase().includes(needle)
      || (p.brand_name || "").toLowerCase().includes(needle));
  }, [packages, q]);
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="cc-compare-picker">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <PackageOpen className="w-4 h-4" /> Choose a package to compare
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <input
            type="text"
            placeholder="Search packages…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-9 px-2 border border-border bg-background text-sm"
            data-testid="cc-compare-picker-search"
            autoFocus
          />
          <div className="border border-border max-h-72 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-6">No active packages found.</div>
            )}
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onSelect(p.id); onClose(); }}
                className={`w-full text-left px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted/40 text-sm ${selectedId === p.id ? "bg-primary/10" : ""}`}
                data-testid={`cc-compare-picker-item-${p.id}`}
              >
                <div className="font-medium truncate">{p.name}</div>
                <div className="text-[10px] text-muted-foreground">
                  {p.brand_name || "—"} · {(p.lines || []).length} line{(p.lines || []).length === 1 ? "" : "s"}
                </div>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="cc-compare-picker-close">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Feb 2026 · Ad-hoc deliverable dialog — manual entry outside any
   package schedule. Keeps the item in `content_items` so it appears
   in every calendar / list / timeline view, but with `ad_hoc=true`
   and `package_line_id=null` so quota / regen logic ignores it.
   ────────────────────────────────────────────────────────────────── */
function NewManualItemDialog({ wsId, defaultMonth, brands, projects, packages, members, onClose, onCreated }) {
  const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const defaultDate = () => {
    // If defaultMonth is YYYY-MM and isn't the current month, pin to its 1st.
    const now = new Date();
    const curMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    if (defaultMonth && defaultMonth !== curMonth) return `${defaultMonth}-01`;
    return todayIso();
  };
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    name: "",
    planned_date: defaultDate(),
    brand_id: "",
    client_id: "",
    project_id: "",
    package_id: "",
    category: "",
    topic: "",
    content_pillar: "",
    channels: [],
    status: "planned",
    effort_hours: "",
    caption: "",
    internal_notes: "",
    primary_assignee_user_id: "",
    spawn_tasks: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    clientsLookup(wsId).then((rows) => setClients(rows || []));
  }, [wsId]);

  // When brand changes, auto-fill client_id if blank.
  useEffect(() => {
    if (!form.brand_id) return;
    const b = brands.find((x) => x.id === form.brand_id);
    if (b?.client_id && !form.client_id) setForm((f) => ({ ...f, client_id: b.client_id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.brand_id]);

  // Filter packages / projects by brand so the pickers aren't noisy.
  const scopedPackages = useMemo(() => {
    if (!form.brand_id) return packages;
    return packages.filter((p) => !p.brand_id || p.brand_id === form.brand_id);
  }, [packages, form.brand_id]);
  const scopedProjects = useMemo(() => {
    if (!form.client_id) return projects;
    return projects.filter((p) => !p.client_id || p.client_id === form.client_id);
  }, [projects, form.client_id]);

  const canSubmit = form.name.trim().length >= 1 && /^\d{4}-\d{2}-\d{2}$/.test(form.planned_date);

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Name and date are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        planned_date: form.planned_date,
        brand_id: form.brand_id || null,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        package_id: form.package_id || null,
        category: form.category || null,
        topic: form.topic.trim() || null,
        content_pillar: form.content_pillar.trim() || null,
        channels: form.channels || [],
        status: form.status || "planned",
        effort_hours: form.effort_hours === "" ? null : Number(form.effort_hours),
        caption: form.caption.trim() || null,
        internal_notes: form.internal_notes.trim() || null,
        primary_assignee_user_id: form.primary_assignee_user_id || null,
        spawn_tasks: !!form.spawn_tasks,
      };
      const { data } = await api.post(`/workspaces/${wsId}/content-calendar/items`, payload);
      toast.success(`Added "${data.name}" to ${data.planned_date}`);
      onCreated?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not add deliverable"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="cc-new-item-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> New ad-hoc deliverable
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Drop a one-off item onto the calendar — campaign surge, client ask, PR moment.
            Not tied to any package line, so it won't collide with the next monthly auto-generate.
          </p>
        </DialogHeader>

        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="label-eyebrow block mb-1">Name <span className="text-rose-600">*</span></span>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Diwali eve teaser reel"
                     className="h-9 rounded-none text-sm" autoFocus
                     data-testid="cc-new-item-name" />
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-1">Planned date <span className="text-rose-600">*</span></span>
              <Input type="date" value={form.planned_date}
                     onChange={(e) => setForm({ ...form, planned_date: e.target.value })}
                     className="h-9 rounded-none text-sm" data-testid="cc-new-item-date" />
            </label>

            <label className="block">
              <span className="label-eyebrow block mb-1">Brand</span>
              <Select value={form.brand_id || "none"} onValueChange={(v) => setForm({ ...form, brand_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9 rounded-none text-sm" data-testid="cc-new-item-brand"><SelectValue placeholder="— choose brand —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-1">Client</span>
              <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9 rounded-none text-sm" data-testid="cc-new-item-client"><SelectValue placeholder="— choose client —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>

            <label className="block">
              <span className="label-eyebrow block mb-1">Project</span>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9 rounded-none text-sm" data-testid="cc-new-item-project"><SelectValue placeholder="— no project —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— no project —</SelectItem>
                  {scopedProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-1">Package</span>
              <Select value={form.package_id || "none"} onValueChange={(v) => setForm({ ...form, package_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9 rounded-none text-sm" data-testid="cc-new-item-package"><SelectValue placeholder="— no package —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— no package —</SelectItem>
                  {scopedPackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>

            <label className="block">
              <span className="label-eyebrow block mb-1">Category</span>
              <Select value={form.category || "_none"} onValueChange={(v) => setForm({ ...form, category: v === "_none" ? "" : v })}>
                <SelectTrigger className="h-9 rounded-none text-sm" data-testid="cc-new-item-category"><SelectValue placeholder="— choose —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— uncategorised —</SelectItem>
                  {(CATEGORIES || []).map((c) => <SelectItem key={c.value || c} value={c.value || c}>{c.label || c}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-1">Primary assignee</span>
              <Select value={form.primary_assignee_user_id || "none"} onValueChange={(v) => setForm({ ...form, primary_assignee_user_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9 rounded-none text-sm" data-testid="cc-new-item-owner"><SelectValue placeholder="— unassigned —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— unassigned —</SelectItem>
                  {(members || []).filter((m) => (m.active !== false)).map((m) => (
                    <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.name || m.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <label className="block md:col-span-2">
              <span className="label-eyebrow block mb-1">Topic</span>
              <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                     placeholder="e.g. Festive countdown week-1"
                     className="h-9 rounded-none text-sm" data-testid="cc-new-item-topic" />
            </label>
            <label className="block md:col-span-2">
              <span className="label-eyebrow block mb-1">Caption / notes</span>
              <Textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
                        placeholder="Optional caption or brief for the writer"
                        rows={3} className="rounded-none text-sm" data-testid="cc-new-item-caption" />
            </label>

            <label className="block">
              <span className="label-eyebrow block mb-1">Est. hours</span>
              <Input type="number" step="0.25" min="0"
                     value={form.effort_hours}
                     onChange={(e) => setForm({ ...form, effort_hours: e.target.value })}
                     placeholder="e.g. 2"
                     className="h-9 rounded-none text-sm" data-testid="cc-new-item-hours" />
            </label>
            <label className="inline-flex items-end gap-2 h-9 self-end">
              <input type="checkbox" checked={form.spawn_tasks}
                     onChange={(e) => setForm({ ...form, spawn_tasks: e.target.checked })}
                     disabled={!form.project_id}
                     data-testid="cc-new-item-spawn-task" />
              <span className="text-xs">
                Also create a task on the project
                {!form.project_id && <span className="text-muted-foreground"> (pick a project to enable)</span>}
              </span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}
                  data-testid="cc-new-item-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || busy}
                  className="rounded-none" data-testid="cc-new-item-create">
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
            Add deliverable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function GenerateDialog({ wsId, defaultMonth, brands, onClose, onGenerated }) {
  const [month, setMonth] = useState(defaultMonth);
  const [brandId, setBrandId] = useState("_all");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inheritAssignee, setInheritAssignee] = useState(true);
  // R289d · Feb 2026 — Weekend / holiday conflict handling.
  const [globalShift, setGlobalShift] = useState("keep");
  const [overrides, setOverrides] = useState({});   // gen_key → 'YYYY-MM-DD' or 'keep'

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/content-calendar/preview`, { month, brand_id: brandId === "_all" ? null : brandId });
      setPreview(r.data);
      setOverrides({});
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setBusy(false); }
  };
  const runGenerate = async () => {
    setBusy(true);
    try {
      // Resolve per-item overrides — 'keep' means "no shift, no override";
      // any explicit date wins over the global strategy.
      const dateOverrides = {};
      Object.entries(overrides).forEach(([gk, val]) => {
        if (val && val !== "global") dateOverrides[gk] = val;
      });
      const r = await api.post(`/workspaces/${wsId}/content-calendar/generate`, {
        month,
        brand_id: brandId === "_all" ? null : brandId,
        inherit_assignee: inheritAssignee,
        default_conflict_shift: globalShift,
        date_overrides: dateOverrides,
      });
      toast.success(`Created ${r.data.created} deliverables · ${r.data.tasks_created} tasks${r.data.conflicts_resolved ? ` · ${r.data.conflicts_resolved} off-day conflict(s) handled` : ""}`);
      onGenerated?.();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Generation failed")); }
    finally { setBusy(false); }
  };

  const setRowOverride = (genKey, val) => {
    setOverrides((prev) => ({ ...prev, [genKey]: val }));
  };
  const conflicts = preview?.conflicts || [];
  const nonConflictItems = (preview?.new_items || []).filter((it) => !it.conflict);

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cc-generate-dialog">
        <DialogHeader><DialogTitle>Generate content calendar</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="label-eyebrow mb-1 inline-block">Month</Label>
            <Input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPreview(null); }} className="h-10 rounded-none" data-testid="cc-gen-month" /></div>
          <div><Label className="label-eyebrow mb-1 inline-block">Scope</Label>
            <Select value={brandId} onValueChange={(v) => { setBrandId(v); setPreview(null); }}>
              <SelectTrigger className="h-10 rounded-none" data-testid="cc-gen-brand"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="_all">All active packages</SelectItem>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <p className="text-xs text-muted-foreground">Only <strong>active</strong> packages generate items. Regeneration is safe &mdash; existing items are never duplicated. Tasks are created per skill in the package&apos;s linked project.</p>
        <label className="flex items-start gap-2 text-xs border border-border px-3 py-2 cursor-pointer hover:bg-muted/30" data-testid="cc-gen-inherit-assignee-label">
          <input
            type="checkbox"
            checked={inheritAssignee}
            onChange={(e) => setInheritAssignee(e.target.checked)}
            className="mt-0.5"
            data-testid="cc-gen-inherit-assignee"
          />
          <span>
            <strong>Inherit default assignee from package</strong>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              When on, each spawned task is assigned to the user set in the package&apos;s skill breakdown. Turn off to generate tasks unassigned — you can bulk-inherit later from the Tasks list.
            </div>
          </span>
        </label>
        {!preview ? (
          <Button onClick={runPreview} disabled={busy} variant="outline" className="rounded-none" data-testid="cc-gen-preview">{busy ? "Building preview…" : "Preview"}</Button>
        ) : (
          <>
            <div className="border border-border bg-muted/30 px-3 py-2 text-sm flex gap-4 flex-wrap" data-testid="cc-preview-summary">
              <span><strong>{preview.new_count}</strong> new deliverables</span>
              <span><strong>{preview.tasks_to_create}</strong> tasks will be created</span>
              {conflicts.length > 0 && (
                <span className="text-amber-700 font-semibold">⚠ {conflicts.length} on weekend / holiday</span>
              )}
              {preview.items_without_project > 0 && <span className="text-amber-600">⚠ {preview.items_without_project} items have no linked project — no tasks will spawn for them</span>}
              {preview.skipped_existing > 0 && <span className="text-muted-foreground">{preview.skipped_existing} already exist (skipped)</span>}
            </div>

            {conflicts.length > 0 && (
              <div className="border border-amber-400 bg-amber-50 dark:bg-amber-950/30" data-testid="cc-conflict-panel">
                <div className="px-3 py-2 border-b border-amber-300 flex items-center gap-3 flex-wrap">
                  <strong className="text-xs uppercase tracking-widest text-amber-900 dark:text-amber-100">Off-day conflicts</strong>
                  <span className="text-[11px] text-muted-foreground flex-1">Global default:</span>
                  <Select value={globalShift} onValueChange={setGlobalShift}>
                    <SelectTrigger className="h-8 w-52 rounded-none text-xs" data-testid="cc-conflict-global"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keep">Keep on that day</SelectItem>
                      <SelectItem value="previous_working">Move to previous working day</SelectItem>
                      <SelectItem value="next_working">Move to next working day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-amber-200" data-testid="cc-conflict-list">
                  {conflicts.map((c, i) => {
                    const val = overrides[c.gen_key] || "global";
                    return (
                      <div key={c.gen_key || i} className="px-3 py-1.5 text-xs grid grid-cols-[110px_1fr_180px] gap-2 items-center" data-testid={`cc-conflict-row-${i}`}>
                        <div>
                          <div className="font-mono">{c.planned_date}</div>
                          <div className="text-[10px] text-amber-700 uppercase tracking-wide">{c.label}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{c.brand_name || c.package_name}</div>
                        </div>
                        <Select value={val} onValueChange={(v) => setRowOverride(c.gen_key, v)}>
                          <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`cc-conflict-override-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="global">Use global default</SelectItem>
                            <SelectItem value={c.previous_working}>Previous working ({c.previous_working})</SelectItem>
                            <SelectItem value={c.next_working}>Next working ({c.next_working})</SelectItem>
                            <SelectItem value={c.planned_date}>Keep on {c.planned_date}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {nonConflictItems.length > 0 && (
              <div className="border border-border divide-y divide-border max-h-40 overflow-y-auto" data-testid="cc-preview-list">
                {nonConflictItems.slice(0, 30).map((it, i) => (
                  <div key={i} className="px-3 py-1.5 text-xs flex items-center gap-3" data-testid={`cc-preview-row-${i}`}>
                    <span className="font-mono text-muted-foreground">{it.planned_date}</span>
                    <span className="font-medium flex-1 truncate">{it.name}</span>
                    <span className="text-muted-foreground">{it.brand_name}</span>
                  </div>
                ))}
                {nonConflictItems.length > 30 && (
                  <div className="px-3 py-1.5 text-[10px] text-muted-foreground italic">…and {nonConflictItems.length - 30} more</div>
                )}
              </div>
            )}
            {preview.new_count === 0 && <div className="text-sm text-muted-foreground">Nothing new to generate for this scope.</div>}
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          {preview && preview.new_count > 0 && (
            <Button onClick={runGenerate} disabled={busy} className="rounded-none" data-testid="cc-gen-confirm">{busy ? "Generating…" : `Generate ${preview.new_count} items & ${preview.tasks_to_create} tasks`}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
